import { alpha } from '@mui/material/styles';
import { gray } from '../getDesignTokens';

export const surfacesCustomizations = {
  // ─── Global scrollbar theming ─────────────────────────────────────
  // Applies a thin, theme-aware scrollbar to all scrollable elements.
  MuiCssBaseline: {
    styleOverrides: (theme) => {
      const isDark = theme.palette.mode === 'dark';
      const thumbColor = isDark ? gray[700] : gray[300];
      const thumbHover = isDark ? gray[500] : gray[400];

      return {
        // Firefox
        '*, *::before, *::after': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${thumbColor} transparent`,
        },
        // Webkit (Chrome, Edge, Safari)
        '*::-webkit-scrollbar': {
          width: 6,
          height: 6,
        },
        '*::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '*::-webkit-scrollbar-thumb': {
          background: thumbColor,
          borderRadius: 3,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: thumbHover,
        },
        '*::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
      };
    },
  },
  MuiAccordion: {
    defaultProps: {
      elevation: 0,
      disableGutters: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 4,
        overflow: 'clip',
        backgroundColor: theme.palette.background.default,
        border: '1px solid',
        borderColor: theme.palette.divider,
        ':before': {
          backgroundColor: 'transparent',
        },
        '&:not(:last-of-type)': {
          borderBottom: 'none',
        },
        '&:first-of-type': {
          borderTopLeftRadius: theme.shape.borderRadius,
          borderTopRightRadius: theme.shape.borderRadius,
        },
        '&:last-of-type': {
          borderBottomLeftRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
        },
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: ({ theme }) => ({
        border: 'none',
        borderRadius: 8,
        '&:hover': { backgroundColor: gray[50] },
        '&:focus-visible': { backgroundColor: 'transparent' },
        ...theme.applyStyles('dark', {
          '&:hover': { backgroundColor: gray[800] },
        }),
      }),
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: { mb: 20, border: 'none' },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          padding: 16,
          gap: 16,
          transition: 'all 100ms ease',
          backgroundColor: gray[50],
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          ...theme.applyStyles('dark', {
            backgroundColor: gray[800],
          }),
          variants: [
            {
              props: {
                variant: 'outlined',
              },
              style: {
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                background: 'hsl(0, 0%, 100%)',
                ...theme.applyStyles('dark', {
                  background: alpha(gray[900], 0.4),
                }),
              },
            },
          ],
        };
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 0,
        '&:last-child': { paddingBottom: 0 },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  // ─── Global Dialog / Modal styling ────────────────────────────────
  // Applies to every <Dialog>, <Modal>, and Drawer in the app for a
  // consistent, modern feel: glassy backdrop, lifted dialog with proper
  // shadow + rounded corners, themed sections.
  MuiBackdrop: {
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        return {
          backgroundColor: isDark
            ? alpha(gray[900], 0.6)
            : alpha(gray[900], 0.45),
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          // Don't blur invisible backdrops (e.g. Tooltip)
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        };
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        return {
          borderRadius: 16,
          border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          backgroundColor: isDark
            ? alpha('#0F172A', 0.95)
            : '#FFFFFF',
          backgroundImage: isDark
            ? `linear-gradient(180deg, ${alpha('#1E293B', 0.6)} 0%, ${alpha('#0F172A', 0.6)} 100%)`
            : `linear-gradient(180deg, ${alpha('#FFFFFF', 1)} 0%, ${alpha(gray[50], 0.6)} 100%)`,
          boxShadow: isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.04)'
            : '0 24px 60px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15, 23, 42, 0.04)',
          transition: 'all 120ms ease',
          overflow: 'hidden',
        };
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2.5, 3),
        fontSize: '1.15rem',
        fontWeight: 700,
        letterSpacing: 0.1,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backgroundColor: 'transparent',
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),
        // Avoid the awkward MUI default that adds padding-top: 0 right
        // after a DialogTitle. We control spacing per-dialog instead.
        '&.MuiDialogContent-root': {
          paddingTop: theme.spacing(3),
        },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1.75, 3),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        backgroundColor: alpha(theme.palette.action.hover, 0.4),
        gap: theme.spacing(1),
      }),
    },
  },
  // Drawer benefits from the same lifted-surface treatment
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        return {
          borderRadius: 0,
          backgroundColor: isDark
            ? alpha('#0F172A', 0.97)
            : '#FFFFFF',
          backgroundImage: 'none',
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          boxShadow: isDark
            ? '-12px 0 40px rgba(0, 0, 0, 0.45)'
            : '-12px 0 40px rgba(15, 23, 42, 0.12)',
        };
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        backgroundColor: theme.palette.background.paper,
        transition: 'all 100ms ease',
        ...theme.applyStyles('dark', {
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
        }),
      }),
    },
  },
  
  
};
